import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { COLLECTIONS } from '../../constants/collections';
import { DEFAULT_ROLE_PERMISSIONS, DEFAULT_OWNER_ROLES, DEFAULT_TRANSPORTER_ROLES, DEFAULT_NOTIFICATION_PREFERENCES } from '../../constants/permissions';

/**
 * Get permissions for a specific tenant.
 * Falls back to default permissions if no custom config exists.
 */
/**
 * Subscribe to tenant permissions in real-time.
 */
export const subscribeToTenantPermissions = (tenantId, callback) => {
    const configRef = doc(db, COLLECTIONS.TENANTS, tenantId, 'config', 'permissions');

    return onSnapshot(configRef, (snap) => {
        if (snap.exists()) {
            const data = snap.data();
            callback({
                rolePermissions: data.rolePermissions || DEFAULT_ROLE_PERMISSIONS,
                ownerRoles: data.ownerRoles || DEFAULT_OWNER_ROLES,
                transporterRoles: data.transporterRoles || DEFAULT_TRANSPORTER_ROLES,
                notificationPreferences: Object.keys(data.notificationPreferences || {}).length > 0 ? data.notificationPreferences : DEFAULT_NOTIFICATION_PREFERENCES
            });
        } else {
            callback({
                rolePermissions: DEFAULT_ROLE_PERMISSIONS,
                ownerRoles: DEFAULT_OWNER_ROLES,
                transporterRoles: DEFAULT_TRANSPORTER_ROLES,
                notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES
            });
        }
    }, (error) => {
        console.error('Error subscribing to permissions:', error);
        callback({
            rolePermissions: DEFAULT_ROLE_PERMISSIONS,
            ownerRoles: DEFAULT_OWNER_ROLES,
            transporterRoles: DEFAULT_TRANSPORTER_ROLES,
            notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES
        });
    });
};

export const getTenantPermissions = async (tenantId) => {
    try {
        const configRef = doc(db, COLLECTIONS.TENANTS, tenantId, 'config', 'permissions');
        const configSnap = await getDoc(configRef);

        if (configSnap.exists()) {
            const data = configSnap.data();
            return {
                rolePermissions: data.rolePermissions || DEFAULT_ROLE_PERMISSIONS,
                ownerRoles: data.ownerRoles || DEFAULT_OWNER_ROLES,
                transporterRoles: data.transporterRoles || DEFAULT_TRANSPORTER_ROLES,
                notificationPreferences: Object.keys(data.notificationPreferences || {}).length > 0 ? data.notificationPreferences : DEFAULT_NOTIFICATION_PREFERENCES
            };
        }

        return {
            rolePermissions: DEFAULT_ROLE_PERMISSIONS,
            ownerRoles: DEFAULT_OWNER_ROLES,
            transporterRoles: DEFAULT_TRANSPORTER_ROLES,
            notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES
        };
    } catch (error) {
        console.error('Error fetching tenant permissions:', error);
        return {
            rolePermissions: DEFAULT_ROLE_PERMISSIONS,
            ownerRoles: DEFAULT_OWNER_ROLES,
            transporterRoles: DEFAULT_TRANSPORTER_ROLES,
            notificationPreferences: DEFAULT_NOTIFICATION_PREFERENCES
        };
    }
}

/**
 * Update custom permissions for a tenant
 */
export const updateTenantPermissions = async (tenantId, rolePermissions, ownerRoles = null, transporterRoles = null, notificationPreferences = null) => {
    try {
        const configRef = doc(db, COLLECTIONS.TENANTS, tenantId, 'config', 'permissions');
        const updateData = {
            rolePermissions,
            updatedAt: new Date()
        };

        if (ownerRoles) {
            updateData.ownerRoles = ownerRoles;
        }

        if (transporterRoles) {
            updateData.transporterRoles = transporterRoles;
        }

        if (notificationPreferences) {
            updateData.notificationPreferences = notificationPreferences;
        }

        await setDoc(configRef, updateData, { merge: true });
    } catch (error) {
        console.error('Error updating tenant permissions:', error);
        throw error;
    }
};

/**
 * Check if a user has a specific permission
 */
export const hasPermission = (userPermissions, feature, action) => {
    if (!userPermissions || !userPermissions[feature]) return false;
    return userPermissions[feature].includes(action);
};
